import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from "@mui/material";
import AvatarCircle from "app/components/AvatarCircle";
import { LargeCustomIcon, MediumCustomIcon } from "../../layer00";
import { OneOnOnePrepModal } from "../../layer16";
import { theme } from "../../../../theme";
import { useDismissIssueMutation } from "app/containers/TeamsTab/teamsCheckInApi";

export type IState = "error" | "warning";

const state2Title: Record<IState, string> = {
  error: "Critical Issue",
  warning: "Potential Issue",
};

const state2TitleColor: Record<IState, string> = {
  error: theme.palette.error.main,
  warning: theme.palette.warning.dark,
};

const state2IconColor: Record<IState, string> = {
  error: "var(--team-app-critical-icon)",
  warning: theme.palette.warning.main,
};

const state2BackgroundColor: Record<IState, string> = {
  error: theme.palette.error.light,
  warning: theme.palette.warning.light,
};

const state2BorderColor: Record<IState, string> = {
  error: "var(--ruby-30)",
  warning: "var(--gold-30)",
};

export type IssueCardProps = {
  state: IState;
  threadId: string;
  userAccountId: number;
  personName?: string;
  comment: string;
  date?: string | null;
};

export const IssueCard = (props: IssueCardProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [dismissIssue, { isLoading: isDismissing }] = useDismissIssueMutation();

  const handleDismiss = () => {
    dismissIssue({ threadId: props.threadId, isDismissed: true });
  };

  return (
    <Card
      sx={{
        border: `1px solid ${state2BorderColor[props.state]}`,
        paddingLeft: 0,
        paddingRight: 0,
        "& .MuiCardHeader-avatar": {
          alignSelf: "start",
          paddingTop: ".25rem",
          marginRight: ".75rem",
        },
      }}
    >
      <CardHeader
        sx={{
          backgroundColor: state2BackgroundColor[props.state],
          borderRadius: "1rem 1rem 0rem 0rem",
          borderBottom: `1px solid ${state2BorderColor[props.state]}`,
          paddingLeft: "1rem",
          paddingRight: "1rem",
        }}
        disableTypography
        avatar={<MediumCustomIcon icon="CRITICAL" stroke={state2IconColor[props.state]} />}
        title={
          <Box>
            <Typography variant="cardTitle" color={state2TitleColor[props.state]}>
              {state2Title[props.state]}
            </Typography>
          </Box>
        }
      ></CardHeader>
      <CardContent>
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          alignItems={{
            xs: "flex-start",
            md: "center",
          }}
          rowGap="1rem"
          columnGap="1rem"
        >
          <AvatarCircle
            userAccountId={props.userAccountId}
            name={props.personName}
            size="large"
          />
          <Box
            display={{
              xs: "none",
              md: "block",
            }}
          >
            <LargeCustomIcon
              icon="SEPERATOR"
              stroke={`${state2BorderColor[props.state]}`}
            />
          </Box>
          <Stack
            flex={{
              xs: "0 0 auto",
              md: "1 1 0",
            }}
            width={{
              xs: "100%",
              md: "auto",
            }}
            minWidth={0}
            direction="column"
            rowGap=".25rem"
          >
            <Typography variant="cardBodyHeader" className="text-sapphire-150">
              {props.personName}
            </Typography>
            <Typography variant="cardBodyRegular">
              {`"${props.comment}"`}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            columnGap="1rem"
            width={{ xs: "100%", md: "auto" }}
            flexShrink={0}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsVisible(() => true)}
              sx={{ flex: { xs: "1 1 0", md: "none" } }}
            >
              Review
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDismiss}
              disabled={isDismissing}
              sx={{ flex: { xs: "1 1 0", md: "none" } }}
            >
              Dismiss
            </Button>
          </Stack>
        </Stack>
      </CardContent>
      {isVisible && (
        <OneOnOnePrepModal
          id=""
          isVisible
          userAccountId={props.userAccountId}
          handleClose={() => setIsVisible(() => false)}
          date={props.date}
        />
      )}
    </Card>
  );
};
